/* ============================================================
   routes.js — the 24-route information architecture.

   This is the one artefact ADR 0013 says to preserve outright: the module
   breakdown, nav grouping and screen inventory are a validated UX artefact
   independent of the fake data behind them. Titles are verbatim from
   js/app.js:7-16 and the group order is verbatim from index.html's sidebar.

   `permission` gates the nav item and the route guard. See auth/permissions.js:
   this is cosmetic — only /users/*, /roles/* and /permissions/* enforce
   server-side. Routes with no matching backend module carry null and are open
   to any signed-in user.
   ============================================================ */

/* Sidebar sections, top to bottom. 'Overview' (Dashboard) has no heading; the
   rest are lines on the subway-map sidebar (Sidebar.jsx). `tone` picks the
   line's colour (.nav-tone-* in styles.css). */
export const NAV_GROUPS = ['Overview', 'Candidates', 'Recruitment', 'Pipeline', 'Insights', 'Team', 'System']
export const NAV_GROUP_META = {
  Overview: { headless: true },
  Candidates: { tone: 'sage' },
  Recruitment: { tone: 'red' },
  Pipeline: { tone: 'amber' },
  Insights: { tone: 'blue' },
  Team: { tone: 'mauve' },
  System: { tone: 'slate' },
}

export const ROUTES = [
  // --- Overview ---
  { path: 'dashboard', title: 'Dashboard', icon: 'dashboard', group: 'Overview', permission: 'dashboard.view' },

  // --- Candidates ---
  { path: 'inbox', title: 'Recruitment Inbox', icon: 'inbox', group: 'Candidates', permission: 'inbox.view', badge: 'inbox' },
  { path: 'matching', title: 'Job Matching', icon: 'target', group: 'Candidates', permission: 'job_matching.view', badge: 'matching', hidden: true },
  { path: 'import', title: 'CV Import', icon: 'upload', group: 'Candidates', permission: 'cv_import.view' },
  { path: 'candidates', title: 'Candidates', icon: 'users', group: 'Candidates', permission: 'candidates.view' },
  // Replaces Talent Pool. That screen browsed candidate accounts over a seed
  // overlay of invented skills and companies; /candidates already does the real
  // version of that. This one holds the people we have no job for yet.
  // No `badge`: the matching badge counts the unassigned queue and reusing the
  // key here would make the same rows read as two separate counts.
  { path: 'cvbank', title: 'CV Bank', icon: 'talent', group: 'Candidates', permission: 'cv_bank.view' },
  { path: 'talent', title: 'Find Talent', icon: 'user-plus', group: 'Candidates', permission: 'talent.view' },
  { path: 'tasks', title: 'Tasks', icon: 'check-square', group: 'Candidates', permission: 'tasks.view', badge: 'tasks', hidden: true },

  // --- Recruitment ---
  { path: 'requisitions', title: 'Requisitions', icon: 'file', group: 'Recruitment', permission: 'requisitions.view' },
  { path: 'jobs', title: 'Jobs', icon: 'briefcase', group: 'Recruitment', permission: 'jobs.view', badge: 'jobs' },
  { path: 'offers', title: 'Offers', icon: 'offers', group: 'Recruitment', permission: 'offers.view' },
  // Requisitions and offers waiting on Director HR. Either approve tag opens it.
  { path: 'approvals', title: 'Approvals', icon: 'check-circle', group: 'Recruitment', permission: ['approvals.view', 'requisitions.approve', 'offers.approve'], badge: 'approvals' },
  { path: 'progress', title: 'Job Progress', icon: 'trending-up', group: 'Recruitment', permission: 'progress.view' },
  { path: 'jobboard', title: 'Job Board', icon: 'layers', group: 'Recruitment', permission: 'job_board.view' },
  // `comingSoon`: faded in the sidebar; the route renders screens/ComingSoon.
  { path: 'aistudio', title: 'AI Studio', icon: 'zap', group: 'Recruitment', permission: null, comingSoon: true },
  { path: 'aiassistant', title: 'AI Assistant', icon: 'sparkles', group: 'Recruitment', permission: null, comingSoon: true },

  // --- Pipeline ---
  { path: 'pipeline', title: 'Pipeline', icon: 'pipeline', group: 'Pipeline', permission: 'pipeline.view' },
  { path: 'interviews', title: 'Interviews', icon: 'calendar', group: 'Pipeline', permission: 'interviews.view' },
  { path: 'assessments', title: 'Assessments', icon: 'check-square', group: 'Pipeline', permission: 'assessments.view' },
  { path: 'calendar', title: 'Calendar', icon: 'calendar', group: 'Pipeline', permission: 'calendar.view' },

  // --- Insights ---
  { path: 'analytics', title: 'Analytics', icon: 'analytics', group: 'Insights', permission: 'analytics.view' },
  { path: 'reports', title: 'Reports', icon: 'reports', group: 'Insights', permission: 'reports.view' },
  { path: 'recruiterhub', title: 'Recruiter Performance', icon: 'check-circle', group: 'Insights', permission: 'recruiter_hub.view' },
  // Reached from the topbar bell (View all), not the sidebar.
  { path: 'notifications', title: 'Notifications', icon: 'bell', group: 'Insights', permission: null, badge: 'notifications', hidden: true },

  // --- Team ---
  { path: 'managers', title: 'Recruiters', icon: 'managers', group: 'Team', permission: 'hiring_managers.view' },
  { path: 'departments', title: 'Departments', icon: 'layers', group: 'Team', permission: 'department.view' },
  { path: 'deptmanagers', title: 'Department Managers', icon: 'users', group: 'Team', permission: 'department_managers.view' },

  // --- System ---
  // Access Control lives in Settings → Access Control now; App.jsx redirects /rbac there.
  // Settings → View opens the page only; each tab (General included) has its own View.
  { path: 'settings', title: 'Settings', icon: 'settings', group: 'System', permission: 'settings.view' },
  { path: 'activity', title: 'System Analytics', icon: 'activity', group: 'System', permission: 'activity.view' },
  { path: 'help', title: 'Help', icon: 'help', group: 'System', permission: null },
  // Reached from the profile menu, not the sidebar. Open to every signed-in user.
  { path: 'profile', title: 'My Profile', icon: 'user', group: 'System', permission: null, hidden: true },
]

export const ROUTE_BY_PATH = Object.fromEntries(ROUTES.map((r) => [r.path, r]))

export const DEFAULT_ROUTE = 'dashboard'
